import { parseISO } from 'date-fns'
import type { Moment } from 'moment'
import moment from 'moment'
import type React from 'react'
import { useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import type { SearchValues } from '@/components/Fraud/FilterPanel'
import FilterPanel, { DEFAULT_SEARCH } from '@/components/Fraud/FilterPanel'
import { toQueryVars } from '@/components/Fraud/utils'
import TwoColumnContainer from '@/components/ListWithFiltersContainer'
import PageContainer from '@/components/PageContainer'
import PaginatedList from '@/components/PaginatedList'
import SubjectCard from '@/components/SubjectCard'
import VisitorPanel from '@/components/VisitorPanel'
import useSavedQueryParam from '@/hooks/useSavedQueryParam'
import useUser from '@/hooks/useUser'
import type { Subject as SubjectT } from '@/lib/apollo/query/get-fraud-subjects'
import { useGetFraudSubjectsQuery } from '@/lib/apollo/query/get-fraud-subjects'

interface SearchState {
  limit: number
  offset: number
  searchValues: SearchValues
}

const SEARCH_KEY = 'fraud-v4'

const DEFAULT_SEARCH_STATE: SearchState = {
  limit: 25,
  offset: 0,
  searchValues: DEFAULT_SEARCH,
}

const PageBody = styled.div`
  display: grid;
  min-height: 0;
  height: 100%;
`

const FraudPage: React.FC = () => {
  const { customerId } = useUser()

  const { queryParamValue: search, onSaveQueryParamValue: setSearch } =
    useSavedQueryParam({
      storageKey: SEARCH_KEY,
      defaultState: DEFAULT_SEARCH_STATE,
    })

  const [draftSearch, setDraftSearch] = useState<SearchState>(search)
  const [lastUpdated, setLastUpdated] = useState<Moment>(moment())

  const history = useHistory()

  const [fetch, { loading, error, data }] = useGetFraudSubjectsQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    onCompleted: data => {
      setLastUpdated(moment())

      if (data.getFraudSubjects.results.length === 0 && search.offset !== 0) {
        onChangePage(search.limit, 0)
      }
    },
  })

  const commitSearchState = useCallback(
    (searchState: SearchState) => {
      setSearch(searchState)
      fetch({
        variables: toQueryVars({
          customerId,
          ...searchState,
        }),
      })
    },
    [fetch, setSearch, customerId],
  )

  const onChangePage = useCallback(
    (limit: number, offset: number) => {
      const newSearch = {
        ...search,
        offset,
        limit,
      }

      setDraftSearch(newSearch)
      commitSearchState(newSearch)
    },
    [commitSearchState, search],
  )

  useEffect(() => {
    if (!customerId) {
      return
    }

    fetch({
      variables: toQueryVars({
        customerId,
        ...search,
      }),
    })
  }, [fetch, customerId, search])

  const setApplication = async (subjectId: string) => {
    history.push('/applicant/' + subjectId)
  }

  const auxSetApplication = async (subjectId: string) => {
    const targetURL = '/applicant/' + subjectId
    window.open(targetURL, '_blank')?.focus()
  }

  if (customerId === undefined) {
    return <></>
  }

  return (
    <PageContainer>
      <PageBody>
        <TwoColumnContainer>
          <FilterPanel
            searchValues={draftSearch.searchValues}
            onChangeSearchValues={searchValues =>
              setDraftSearch({
                ...draftSearch,
                searchValues,
              })
            }
            onSubmitSearchValues={newSearchValues => {
              const newSearch = {
                ...search,
                offset: DEFAULT_SEARCH_STATE.offset,
                searchValues: newSearchValues,
              }

              setSearch(newSearch)
            }}
          />
          <PaginatedList<SubjectT>
            loading={loading}
            lastUpdated={lastUpdated}
            total={data?.getFraudSubjects?.total ?? 0}
            error={error}
            limit={search.limit}
            offset={search.offset}
            onChangePage={onChangePage}
            data={data?.getFraudSubjects?.results ?? []}
            row={subject => (
              <SubjectCard
                avatarUrl={subject.avatar}
                onClick={() => setApplication(subject.subjectId)}
                onAuxClick={() => auxSetApplication(subject.subjectId)}
                customerClientName={subject.customerClientName}
                name={subject.name}
                email={subject.email ?? ''}
                type={subject.type ?? 'tenant'}
                address={subject.address}
                milestones={subject.milestones}
                tenancyStartDate={parseISO(subject.tenancyStartDate)}
                visitorPanel={<VisitorPanel visitors={subject.visitors} />}
                referenceTier={subject.referenceTier}
                flaggedAsFraudAt={parseISO(subject.flaggedAsFraudAt)}
              />
            )}
          />
        </TwoColumnContainer>
      </PageBody>
    </PageContainer>
  )
}

export default FraudPage
